import React, {
  useCallback,
  useState,
  type ComponentType,
  type SVGProps,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils';
import CopyIcon from '@/shared/assets/copy-icon.svg?react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui';

interface CopyButtonProps {
  value: string;
  onCopy?: (value: string) => void;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  variant?: 'default' | 'ghost' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  tooltipText?: string;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  tooltipOffset?: number;
  ariaLabel?: string;
  disabled?: boolean;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
}

const sizeClasses = {
  sm: 'size-[1.8rem]',
  md: 'size-[2.4rem]',
  lg: 'size-[3.2rem]',
};

export const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      value,
      onCopy,
      className,
      buttonClassName,
      iconClassName,
      variant = 'secondary',
      size = 'md',
      showTooltip = true,
      tooltipText,
      tooltipSide = 'top',
      tooltipOffset = 8,
      ariaLabel,
      disabled = false,
      icon: Icon = CopyIcon,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = useCallback(async () => {
      if (disabled) return;

      const copiedText = value || '';

      try {
        await navigator.clipboard.writeText(copiedText);
        setIsCopied(true);
        onCopy?.(copiedText);
      } catch {
        console.error('Failed to copy to clipboard');
      }
    }, [value, onCopy, disabled]);

    const handleAutoHide = useCallback(() => {
      setIsCopied(false);
    }, []);

    const buttonElement = (
      <Button
        ref={ref}
        variant={variant}
        onClick={handleCopy}
        disabled={disabled}
        className={cn(
          'group flex items-center justify-center hover:bg-transparent-bg bg-transparent-bg transition-colors text-foreground shadow-none px-[0.8rem] py-[0.8rem]',
          sizeClasses[size],
          buttonClassName
        )}
        aria-label={ariaLabel || t('common.copy')}>
        <Icon
          className={cn(
            'text-current group-hover:text-muted-foreground transition-colors size-[100%]',
            iconClassName
          )}
        />
      </Button>
    );

    if (!showTooltip) {
      return <div className={className}>{buttonElement}</div>;
    }

    return (
      <div className={className}>
        <Tooltip
          open={isCopied}
          showDuration={2000}
          onAutoHide={handleAutoHide}>
          <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
          <TooltipContent
            side={tooltipSide}
            sideOffset={tooltipOffset}
            className="p4">
            {tooltipText || t('common.copied')}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }
);

CopyButton.displayName = 'CopyButton';
